import { yupResolver } from '@hookform/resolvers/yup'
import { Flex, Tooltip } from 'antd'
import { ColumnsType } from 'antd/es/table'
import classNames from 'classnames'
import BaseAutocomplete from 'components/common/BaseAutocomplete'
import BaseButton from 'components/common/BaseButton'
import BaseInputText from 'components/common/BaseInputText'
import BaseModal from 'components/common/BaseModal'
import BaseSwitch from 'components/common/BaseSwitch'
import BaseTable from 'components/common/BaseTable'
import BaseTableAction from 'components/common/BaseTable/BaseTableAction'
import BaseTitle from 'components/common/BaseTitle'
import IconPlus from 'components/icons/IconPlus'
import { API_KEY } from 'constants/react-query'
import { ROUTE } from 'constants/route'
import { TRANSLATE, UNIT_TRANSLATE } from 'constants/translate'
import { EModalType, EStatus, ROLE } from 'enums/common'
import { EKPIUnit } from 'enums/kpi'
import useNotification from 'hooks/useNotification'
import useTableParams from 'hooks/useTableParams'
import { modalProps } from 'models/common'
import { IDepartmentDetail } from 'models/department'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { Link } from 'react-router-dom'
import {
  createDepartment,
  deleteDepartment,
  getDepartmentList,
  toggleStatusDepartment,
  updateDepartment
} from 'services/department.service'
import { getManagerList } from 'services/manager.service'
import { useAppSelector } from 'store'
import { getAuthFromStore } from 'store/slices/auth'
import { formatYMD, r } from 'utils/common'
import NumberUtil from 'utils/number'
import { createDepartmentSchema, VALIDATIONS } from 'validation/admin'

type departmentModal = IDepartmentDetail & modalProps

function Department() {
  const n = useNotification()
  const { tableParams, searchParams } = useTableParams<IDepartmentDetail>()
  const { t } = useTranslation()
  const infoAccount = useAppSelector(getAuthFromStore)
  const queryClient = useQueryClient()
  const [department, setDepartment] = useState<departmentModal>()
  const [activeSwitch, setActiveSwitch] = useState(false)
  const isRoleDepHead = infoAccount.role === ROLE.DHEAD
  const { handleSubmit, control, reset, setValue } = useForm({
    resolver: yupResolver(createDepartmentSchema),
    mode: 'all'
  })

  useEffect(() => {
    setValue('clientId', infoAccount.clientId)
    setValue('departmentName', department?.name)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [department])

  const { data, isLoading } = useQuery({
    queryKey: [API_KEY.DEPARTMENT_LIST, searchParams],
    queryFn: async () => await getDepartmentList({ ...searchParams, clientId: infoAccount.clientId }),
    onError: n.handleUseQueryError()
  })

  const { data: dataManagerList, refetch: refetchManagerList } = useQuery({
    queryKey: ['managerList', infoAccount.clientId],
    queryFn: async () => await getManagerList({ clientId: infoAccount.clientId, role: ROLE.DHEAD }),
    enabled: infoAccount.role !== ROLE.DHEAD,
    onError: n.handleUseQueryError()
  })

  const getManagerName = (id: number) => {
    const data = dataManagerList.items
    if (data) {
      const resolver = data.filter((item: any) => item.id === id)
      return resolver[0].name
    }
  }

  const showNotificationSuccess = (departmentParam?: departmentModal) => {
    const admin = departmentParam?.admin
    const isDeleted = admin?.deletedAt != null
    const hasValidId = admin?.id !== undefined

    switch (departmentParam?.type) {
      case EModalType.CREATE:
        n.handleApiSuccess(
          t('admin.department.modal_create.title_success', { department_name: departmentParam.name }),
          !hasValidId
            ? ''
            : t('admin.department.modal_create.desc_success', {
                manager_name: hasValidId && getManagerName(admin.id)
              })
        )
        break

      case EModalType.UPDATE:
        n.handleApiSuccess(
          t('admin.department.modal_update.title_success', { department_name: departmentParam.name }),
          isDeleted || !hasValidId
            ? ''
            : t('admin.department.modal_update.desc_success', {
                manager_name: hasValidId && getManagerName(admin.id)
              })
        )
        break

      case EModalType.DELETE:
        n.handleApiSuccess(
          t('admin.department.modal_delete.title_success', { department_name: departmentParam.name }),
          isDeleted || !hasValidId
            ? ''
            : t('admin.department.modal_delete.desc_success', {
                manager_name: hasValidId && getManagerName(admin.id)
              })
        )
        break

      case EModalType.DISABLE:
        n.handleApiSuccess(
          t('admin.department.modal_disable.title_success', { department_name: departmentParam.name }),
          isDeleted || !hasValidId
            ? ''
            : t('admin.department.modal_disable.desc_success', {
                manager_name: hasValidId && getManagerName(admin.id)
              })
        )
        break

      case EModalType.ENABLE:
        n.handleApiSuccess(
          t('admin.department.modal_enable.title_success', { department_name: departmentParam.name }),
          isDeleted || !hasValidId
            ? ''
            : t('admin.department.modal_enable.desc_success', {
                manager_name: hasValidId && getManagerName(admin.id)
              })
        )
        break

      default:
        break
    }
  }

  const getErrorMessageUpdate = (department?: departmentModal) => {
    switch (department?.type) {
      case EModalType.UPDATE:
        return t('admin.department.modal_update.title_error', { department_name: department?.name })

      case EModalType.DISABLE:
        return t('admin.department.modal_disable.title_error', { department_name: department?.name })

      case EModalType.ENABLE:
        return t('admin.department.modal_enable.title_error', { department_name: department?.name })

      case EModalType.DELETE:
        return t('admin.department.modal_delete.title_error', { department_name: department?.name })

      default:
        return ''
    }
  }

  const renderDepartmentCell = (value: number | null) => {
    return value || value === 0
      ? NumberUtil.format(value.toFixed(2), { suffix: UNIT_TRANSLATE[EKPIUnit.PERCENT] })
      : TRANSLATE.NA
  }

  // handle modal info
  const onCreate = () => {
    setDepartment({
      id: 1,
      status: false,
      name: '',
      type: EModalType.CREATE,
      title: t('admin.department.modal_create.title'),
      description: t('admin.department.modal_create.description'),
      okText: t('common.btn_create')
    })
    refetchManagerList()
  }

  const onUpdate = (record: IDepartmentDetail) => {
    setDepartment({
      ...record,
      type: EModalType.UPDATE,
      okText: t('common.btn_save'),
      title: t('admin.department.modal_update.title'),
      description: t('admin.department.modal_update.description')
    })
    refetchManagerList()
    reset({
      name: record?.name,
      adminId: record.admin?.deletedAt ? undefined : record?.admin?.id
    })
  }

  const onDelete = (record: IDepartmentDetail) => {
    setDepartment({
      ...record,
      type: EModalType.DELETE,
      okText: t('common.btn_delete'),
      title: t('admin.department.modal_delete.title'),
      description: t('admin.department.modal_delete.description')
    })
  }

  const onToggleStatus = (record: IDepartmentDetail) => {
    if (record?.status) {
      setDepartment({
        ...record,
        type: EModalType.DISABLE,
        title: t('admin.department.modal_disable.title'),
        description: t('admin.department.modal_disable.description'),
        okText: t('common.btn_disable')
      })
    } else {
      setDepartment({
        ...record,
        type: EModalType.ENABLE,
        title: t('admin.department.modal_enable.title'),
        description: t('admin.department.modal_enable.description'),
        okText: t('common.btn_enable')
      })
    }
  }

  // handle logic api
  const { mutate: mutateCreate, isLoading: isLoadingCreate } = useMutation({
    mutationFn: createDepartment,
    onError: n.handleApiError((req) => t('admin.department.modal_create.title_error', { department_name: req.name })),
    onSuccess: (_, req) => {
      showNotificationSuccess({
        ...department,
        name: req.name,
        admin: { id: req.adminId }
      } as departmentModal)
      handleCancel()
      queryClient.invalidateQueries({ queryKey: [API_KEY.DEPARTMENT_LIST] })
      queryClient.invalidateQueries({ queryKey: [API_KEY.KPI_LIST] })
      queryClient.invalidateQueries({ queryKey: [API_KEY.STAFF_LIST] })
    }
  })

  const { mutate: mutateUpdate, isLoading: isLoadingUpdate } = useMutation({
    mutationFn: updateDepartment,
    onError: n.handleApiError(getErrorMessageUpdate(department)),
    onSuccess: (_, req) => {
      showNotificationSuccess(
        req
          ? ({
              ...department,
              name: req.params.name,
              admin: {
                ...department?.admin,
                id: req.params.adminId
              }
            } as departmentModal)
          : department
      )
      handleCancel()
      queryClient.invalidateQueries({ queryKey: [API_KEY.DEPARTMENT_LIST] })
      queryClient.invalidateQueries({ queryKey: [API_KEY.KPI_LIST] })
      queryClient.invalidateQueries({ queryKey: [API_KEY.STAFF_LIST] })
    }
  })

  const { mutate: mutateDelete, isLoading: isLoadingDelete } = useMutation({
    mutationFn: deleteDepartment,
    onError: n.handleApiError(getErrorMessageUpdate(department)),
    onSuccess: () => {
      showNotificationSuccess(department)
      handleCancel()
      queryClient.invalidateQueries({ queryKey: [API_KEY.DEPARTMENT_LIST] })
      queryClient.invalidateQueries({ queryKey: [API_KEY.KPI_LIST] })
      queryClient.invalidateQueries({ queryKey: [API_KEY.STAFF_LIST] })
    }
  })

  const { mutate: toggleStatus, isLoading: isLoadingToggle } = useMutation({
    mutationFn: toggleStatusDepartment,
    onError: n.handleApiError(getErrorMessageUpdate(department)),
    onSuccess: () => {
      showNotificationSuccess({
        ...department
      } as departmentModal)

      handleCancel()
      queryClient.invalidateQueries({ queryKey: [API_KEY.DEPARTMENT_LIST] })
      queryClient.invalidateQueries({ queryKey: [API_KEY.KPI_LIST] })
    }
  })

  const handleToggleStatus = () => {
    if (department?.id) {
      toggleStatus(department.id)
    }
  }
  const handleDelete = () => {
    if (department?.id) {
      mutateDelete(department.id)
    }
  }

  const handleCancel = () => {
    reset({
      name: '',
      adminId: undefined
    })
    setDepartment(undefined)
  }

  const handleCreate = () => {
    handleSubmit((values) => {
      const req = {
        name: values.name,
        adminId: values.adminId,
        clientId: infoAccount.clientId ? infoAccount.clientId : 0
      }
      mutateCreate(req)
    })()
  }

  const handleUpdate = () => {
    handleSubmit((values) => {
      const req = {
        name: values.name,
        adminId: values.adminId,
        clientId: infoAccount.clientId ? infoAccount.clientId : 0
      }
      if (department?.id) {
        mutateUpdate({
          id: department.id,
          params: req
        })
      }
    })()
  }

  const handleModalConfirm = (type: EModalType) => {
    if (type === EModalType.CREATE) {
      return handleCreate()
    }
    if (type === EModalType.DELETE) {
      return handleDelete()
    }
    if ([EModalType.DISABLE, EModalType.ENABLE].includes(type)) {
      return handleToggleStatus()
    }
    return handleUpdate()
  }

  const tableColumnDepartment: ColumnsType<IDepartmentDetail> = [
    // {
    //   title: t('admin.department.columns.id'),
    //   dataIndex: 'id',
    //   key: 'id',
    //   sorter: true
    // },
    {
      title: t('admin.department.columns.department'),
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      ellipsis: true,
      render: (name, record) => (
        <Link className='text-sm text-neutral-700 underline' to={r(ROUTE.ADMIN.DEPARTMENT_DETAIL, { id: record.id })}>
          <Tooltip placement='topLeft' title={name}>
            <span>{name}</span>
          </Tooltip>
        </Link>
      )
    },
    {
      title: t('admin.department.columns.manager'),
      dataIndex: 'admin',
      key: 'admin',
      sorter: true,
      ellipsis: true,
      render: (_, record) => {
        return record.admin?.deletedAt || record.admin == undefined ? (
          '-'
        ) : (
          <Tooltip placement='topLeft' title={record.admin?.name}>
            <span>{record.admin?.name}</span>
          </Tooltip>
        )
      }
    },
    {
      title: t('admin.department.columns.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <span
          className={classNames(
            'text-center px-2 font-medium leading-5 py-0.5 rounded',
            {
              'bg-leaf-light text-leaf-strong': status
            },
            {
              'bg-tomato-light text-tomato-strong': !status
            }
          )}
        >
          {t(`common.${status ? EStatus.ENABLE : EStatus.DISABLE}`)}
        </span>
      )
    },
    {
      title: t('admin.department.columns.createdAt'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: true,
      defaultSortOrder: 'descend',
      render: (val) => formatYMD(val)
    },
    ...(isRoleDepHead
      ? []
      : [
          {
            title: t('admin.department.columns.action'),
            key: 'action',
            width: 148,
            render: (_: any, record: IDepartmentDetail) => (
              <BaseTableAction
                onDelete={() => onDelete(record)}
                onToggleStatus={() => onToggleStatus(record)}
                onEdit={() => onUpdate(record)}
                status={record.status}
              />
            )
          }
        ])
  ]

  const tableColumnDepartmentActive: ColumnsType<IDepartmentDetail> = [
    {
      title: t('admin.department.columns.department'),
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      ellipsis: true,
      render: (name, record) => {
        return (
          <Link className='text-sm text-neutral-700 underline' to={r(ROUTE.ADMIN.DEPARTMENT_DETAIL, { id: record.id })}>
            <Tooltip placement='topLeft' title={name}>
              <span>{name}</span>
            </Tooltip>
          </Link>
        )
      }
    },
    {
      title: t('admin.department.columns.q1'),
      dataIndex: 'kpiQ1',
      key: 'kpiQ1',
      sorter: true,
      render: renderDepartmentCell
    },
    {
      title: t('admin.department.columns.q2'),
      dataIndex: 'kpiQ2',
      key: 'kpiQ2',
      sorter: true,
      render: renderDepartmentCell
    },
    {
      title: t('admin.department.columns.q3'),
      dataIndex: 'kpiQ3',
      key: 'kpiQ3',
      sorter: true,
      render: renderDepartmentCell
    },
    {
      title: t('admin.department.columns.q4'),
      dataIndex: 'kpiQ4',
      key: 'kpiQ4',
      sorter: true,
      render: renderDepartmentCell
    }
  ]

  return (
    <div>
      <Flex className='justify-between items-center mb-5'>
        <BaseTitle title={t('admin.department.title')} />
        <Flex className='items-center gap-5'>
          <BaseSwitch
            label={t('admin.department.view_result')}
            onChange={(e) => setActiveSwitch(e)}
            name=''
            value={activeSwitch}
          />
          {isRoleDepHead ? null : (
            <BaseButton
              type='primary'
              className={'bg-primary-strong items-center'}
              icon={<IconPlus />}
              onClick={onCreate}
            >
              {t('admin.department.btn_create')}
            </BaseButton>
          )}
        </Flex>
      </Flex>
      <BaseTable<IDepartmentDetail>
        loading={isLoading}
        pagination={false}
        total={data?.length}
        dataSource={data?.items}
        className='mt-6'
        columns={activeSwitch ? [...tableColumnDepartmentActive] : [...tableColumnDepartment]}
        {...tableParams}
      />
      {department?.id && (
        <BaseModal
          width={480}
          open={!!department.id}
          title={department.title}
          description={department.description}
          type={department.type}
          handleCancel={handleCancel}
          onOk={() => handleModalConfirm(department.type)}
          okText={department.okText}
          cancelText={department.cancelText}
          isLoading={isLoadingCreate || isLoadingUpdate || isLoadingDelete || isLoadingToggle}
        >
          {[EModalType.CREATE, EModalType.UPDATE].includes(department.type) && (
            <form>
              <div className='h-full'>
                <div className='mb-6 mt-6'>
                  <BaseInputText
                    label={t('admin.department.form.department_name')}
                    maxLength={VALIDATIONS.MAX_LENGTH_TEXT}
                    name='name'
                    control={control}
                    placeholder={t('admin.department.form.placehoder_department_name')}
                  />
                </div>
                <div className='mb-6'>
                  <BaseAutocomplete
                    control={control}
                    name='adminId'
                    className='w-full'
                    label={t('admin.department.form.manager')}
                    placeholder={t('admin.department.form.placehoder_manager')}
                    getOptionsList={(params) =>
                      getManagerList({
                        ...params,
                        role: ROLE.DHEAD,
                        sortBy: 'createdAt',
                        direction: 'desc',
                        status: true
                      })
                    }
                  />
                </div>
              </div>
            </form>
          )}
        </BaseModal>
      )}
    </div>
  )
}
export default Department
